import React, { useCallback, useState } from "react";
import Delete from "../../../assets/deleteIconRed.svg";
import { QuestionTypeEnum } from "@propertelligent/client-api";
import AddTraspButton from "../../firstTimeUserExperience/commonCom/AddTraspButton";
import { GetImages } from "../../../utils/GetImages";
import CustomInputs from "../../common/Inputs";
import RadioButton from "../../common/RadioButton";
import NewSelectAuto from "../../tenants/NewSelectAuto";
import { convertENUMtoJSONArrayWithSpace } from "../../../utils/helper";
import EmptyState from "../../../newComponents/emptystate";
import { TextCss } from "../../GlobalCss/TextCss";


const QuestionHistoryEdit = ({ postData, setPostData }) => {
  const [activeFollowUp, setActiveFollowUp] = useState({
    questionId: null,
    choiceId: null,
  });

  // Utility: safely get questions array
  const getQuestions = () =>
    postData?.leaseApplicationTemplateSections?.Questionnaires?.questions || [];

  // Helper: write back questions to postData
  const updateQuestionsInState = (updatedQuestions) => {
    
    setPostData((prev) => ({
      ...prev,
      leaseApplicationTemplateSections: {
        ...(prev?.leaseApplicationTemplateSections || {}),
        Questionnaires: {
          ...((prev?.leaseApplicationTemplateSections || {}).Questionnaires || {}),
          questions: updatedQuestions,
        },
      },
    }));
  };

  // Add a new question (generate id based on max existing id + 1)
  const handleAddQuestion = () => {
    const questions = getQuestions();
    const maxId = questions.reduce((m, q) => Math.max(m, q.id || 0), 0);
    const newQuestion = {
      id: maxId + 1,
      questionText: "",
      isRequired: false,
      questionTypeId: QuestionTypeEnum.SingleChoice,
      choices: [
        { id: 1, optionText: "Click to change", isRadio: true },
        { id: 2, optionText: "Click to change", isRadio: true },
      ],
      followUpQuestionText: "",
      followUpQuestionChoiceId: null,
      selectedChoices: [],
    };

    updateQuestionsInState([...questions, newQuestion]);
  };

  // Change question text by questionId
  const handleChangeQuestionText = (questionId, value) => {
    const updated = getQuestions().map((q) =>
      q.id === questionId ? { ...q, questionText: value } : q
    );
    updateQuestionsInState(updated);
  };

  // Change question type (by questionId) and reset related fields
  const handleQuestionChange = (questionId, newType) => {
    
    const updated = getQuestions().map((q) => {
      if (q.id !== questionId) return q;
      const typeNum = Number(newType);
      const newQ = { ...q, questionTypeId: typeNum };
      if(typeNum !== QuestionTypeEnum.SingleChoice){
      newQ.followUpQuestionChoiceId = 0;
      }
      if (typeNum === QuestionTypeEnum.SingleChoice) {
        newQ.followUpQuestionChoiceId = newQ.followUpQuestionChoiceId || 0;
        newQ.selectedChoices = [];
        newQ.choices = (newQ.choices || []).map((c) => ({
          ...c,
          isSelected: false,
          isRadio: true,
        }));
      } else if (typeNum === QuestionTypeEnum.MultipleChoice) {
        newQ.selectedChoices = newQ.selectedChoices || [];
        newQ.choices = (newQ.choices || []).map((c) => ({
          ...c,
          isSelected: !!c.isSelected,
          isRadio: false,
        }));
      }
      return newQ;
    });

    updateQuestionsInState(updated);
  };

  // Add option to a question using questionId (generate new choice id by max + 1)
  const handleAddOption = (questionId) => {
    const updated = getQuestions().map((q) => {
      if (q.id !== questionId) return q;
      const maxChoiceId = (q.choices || []).reduce((m, c) => Math.max(m, c.id || 0), 0);
      const newChoice = {
        id: maxChoiceId + 1,
        optionText: `Option ${maxChoiceId + 1}`,
        isRadio: q.questionTypeId === QuestionTypeEnum.SingleChoice,
      };
      return { ...q, choices: [...(q.choices || []), newChoice] };
    });

    updateQuestionsInState(updated);
  };

  // Remove option by questionId + choiceId
  const handleRemoveOption = (questionId, choiceId) => {
    const updated = getQuestions().map((q) =>
      q.id === questionId ? { ...q, choices: (q.choices || []).filter((c) => c.id !== choiceId) } : q
    );

    // If currently active follow-up belongs to removed choice, clear it
    if (activeFollowUp.choiceId === choiceId && activeFollowUp.questionId === questionId) {
      setActiveFollowUp({ questionId: null, choiceId: null });
    }

    updateQuestionsInState(updated);
  };

  // Change option text using questionId + choiceId
  const handleChangeText = (questionId, choiceId, newValue) => {
    const updated = getQuestions().map((q) =>
      q.id === questionId
        ? {
          ...q,
          choices: (q.choices || []).map((c) =>
            c.id === choiceId ? { ...c, optionText: newValue } : c
          ),
        }
        : q
    );
    updateQuestionsInState(updated);
  };

  // Select / toggle choice (works for single and multiple choice) by ids
  const handleOnClickElement = (questionId, choiceId, questionTypeId) => {
    const updated = getQuestions().map((q) => {
      if (q.id !== questionId) return q;

      if (questionTypeId === QuestionTypeEnum.MultipleChoice) {
        const selected = q.selectedChoices || [];
        const exists = selected.includes(choiceId);
        return {
          ...q,
          selectedChoices: exists ? selected.filter((id) => id !== choiceId) : [...selected, choiceId],
          choices: (q.choices || []).map((c) =>
            c.id === choiceId ? { ...c, isSelected: !exists } : c
          ),
        };
      }

      // Single choice: mark radio and set followUpQuestionChoiceId
      return {
        ...q,
        followUpQuestionChoiceId: choiceId,
        choices: (q.choices || []).map((c) => ({ ...c, isRadio: c.id === choiceId })),
      };
    });

    updateQuestionsInState(updated);
  };

  // Change follow-up question text for questionId
  const handleChangeFollowup = (questionId, value) => {
    const updated = getQuestions().map((q) =>
      q.id === questionId ? { ...q, followUpQuestionText: value } : q
    );
    updateQuestionsInState(updated);
  };

  // Toggle / open follow-up for specific questionId + choiceId
  const handleShowFollowUpInput = (questionId, choiceId) => {
    // If clicking the same active follow-up => close it and clear follow-up on question
    const isSame =
      activeFollowUp.questionId === questionId && activeFollowUp.choiceId === choiceId;

    if (isSame) {
      // Close & clear values
      setActiveFollowUp({ questionId: null, choiceId: null });

      // Also clear followUp fields on question
      const updated = getQuestions().map((q) =>
        q.id === questionId ? { ...q, followUpQuestionText: "", followUpQuestionChoiceId: null } : q
      );
      updateQuestionsInState(updated);
      return;
    }

    // Open selected follow-up and set followUpQuestionChoiceId on question
    setActiveFollowUp({ questionId, choiceId });

    const updated = getQuestions().map((q) =>
      q.id === questionId ? { ...q, followUpQuestionChoiceId: choiceId } : q
    );
    updateQuestionsInState(updated);
  };

  // Close follow-up without clearing followUpQuestionChoiceId (if you want to keep it, else clear)
  const handleHideFollowUp = () => {
    setActiveFollowUp({ questionId: null, choiceId: null });
  };

  // Delete entire question by questionId
  const deleteQuestion = useCallback(
    (questionId) => {
      const updatedQuestions = getQuestions().filter((q) => q.id !== questionId);

      // If removed question was active followup, clear it
      if (activeFollowUp.questionId === questionId) {
        setActiveFollowUp({ questionId: null, choiceId: null });
      }

      // If no questions left, remove Questionnaires entirely
      if (updatedQuestions.length === 0) {
        setPostData((prevData) => {
          const newData = { ...prevData };
          if (newData?.leaseApplicationTemplateSections?.Questionnaires) {
            const las = { ...newData.leaseApplicationTemplateSections };
            delete las.Questionnaires;
            return { ...newData, leaseApplicationTemplateSections: las };
          }
          return newData;
        });
        return;
      }

      updateQuestionsInState(updatedQuestions);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postData, setPostData, activeFollowUp]
  );

  // Copy question by id (append copy to questions with new id)
  const copyQuestion = useCallback(
    (questionId) => {
      const questions = getQuestions();
      const toCopy = questions.find((q) => q.id === questionId);
      if (!toCopy) return;
      const maxId = questions.reduce((m, q) => Math.max(m, q.id || 0), 0);
      const newCopy = {
        ...JSON.parse(JSON.stringify(toCopy)),
        id: maxId + 1,
        // optionally regenerate choice ids
        choices: (toCopy.choices || []).map((c, idx) => ({ ...c, id: (idx + 1) + maxId + 1 })),
      };
      updateQuestionsInState([...questions, newCopy]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postData]
  );

  // Toggle isRequired
  const handleToggleChange = (questionId) => {
    const updated = getQuestions().map((q) =>
      q.id === questionId ? { ...q, isRequired: !q.isRequired } : q
    );
    updateQuestionsInState(updated);
  };

  // Drag & drop using questionId (store dragged questionId in dataTransfer)
  const handleDragStart = (e, questionId) => {
    e.dataTransfer.setData("draggedQuestionId", String(questionId));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };
  const handleDrop = (e, dropQuestionId) => {
    e.preventDefault();
    const draggedQuestionId = Number(e.dataTransfer.getData("draggedQuestionId"));
    if (!draggedQuestionId || draggedQuestionId === dropQuestionId) return;

    const questions = getQuestions().slice();
    const draggedIndex = questions.findIndex((q) => q.id === draggedQuestionId);
    const dropIndex = questions.findIndex((q) => q.id === dropQuestionId);
    if (draggedIndex === -1 || dropIndex === -1) return;

    const [dragged] = questions.splice(draggedIndex, 1);
    questions.splice(dropIndex, 0, dragged);
    updateQuestionsInState(questions);
  };

  // Map enum options for question type select (existing helper)
  const questionTypeOption = convertENUMtoJSONArrayWithSpace(QuestionTypeEnum).filter(
    (item) => item.name !== "Date"
  );

  const questions = getQuestions();


  const enableChoiceEdit = (questionId, choiceId) => {
    const updated = getQuestions().map((q) =>
      q.id === questionId
        ? {
          ...q,
          choices: q.choices.map((c) =>
            c.id === choiceId ? { ...c, isEditing: true } : { ...c, isEditing: false }
          ),
        }
        : q
    );
    updateQuestionsInState(updated);
  };

  return (
    <>
      <section className="question-history">
        {!questions || questions.length === 0 ? (
          <EmptyState
            heading={"No Section Available"}
            actionNode={
              <AddTraspButton
                bname={"Add Question"}
                iconPosition={"right"}
                imageValue={GetImages.BlueAdd}
                onClick={handleAddQuestion}
                border={true}
              />
            }
            isBorder={false}
          />
        ) : (
          <div className="quitionnaire_list">
            {questions.map((question) => (
              <div
                className="questionnaire_edit question-item"
                key={question.id}
                draggable
                onDragStart={(e) => handleDragStart(e, question.id)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, question.id)}
              >
                <div className="questionnaire_edit_option">
                  <div className="questionnaire_edit_option_left ">
                    <img src={GetImages.GripDots} alt="drag" />
                    <CustomInputs
                      type="text"
                      value={question.questionText || ""}
                      isError={false}
                      placeholder="Add Question text here"
                      myClassName={"myClassName"}
                      width={"100%"}
                      onChange={(e) => handleChangeQuestionText(question.id, e.target.value)}
                      maxLength={250}
                      containerClassName="templateInputWrapper"
                    />
                  </div>

                  <div className="questionnaire_edit_option_right">
                    <NewSelectAuto
                      disabled={undefined}
                      setDisabled={undefined}
                      selected={question?.questionTypeId}
                      setSelected={(value) => handleQuestionChange(question.id, value)}
                      setSelectedOption={undefined}
                      selectedOption={undefined}
                      data={questionTypeOption}
                      title={"Wait till the data fetches"}
                      type="Type"
                      error={false}
                      setError={() => { }}
                      fromContractor={true}
                      label={undefined}
                      width={"170px"}
                    />
                  </div>
                </div>

                {(question.questionTypeId === QuestionTypeEnum.SingleChoice ||
                  question.questionTypeId === QuestionTypeEnum.MultipleChoice) && (
                    <div className="radio-buttons flex-column align-items-start">
                      {(question.choices || []).map((option) => {
                        const isActiveFollowUp =
                          activeFollowUp.questionId === question.id &&
                          activeFollowUp.choiceId === option.id;

                        const disableFollowUpBtns =
                          activeFollowUp.choiceId !== null &&
                          !(activeFollowUp.questionId === question.id && activeFollowUp.choiceId === option.id);

                        return (
                          <div key={option.id} className="choice-row-wrapper w-100">
                            <div className="custom-radio-wrapper d-flex justify-content-between w-100">
                              <RadioButton
                                type={
                                  question.questionTypeId === QuestionTypeEnum.SingleChoice ? "radio" : "checkbox"
                                }
                                id={`q${question.id}-c${option.id}`}
                                mainClassName="flex-column"
                                // label={
                                //   option.isOnHover ? (
                                //     <CustomInputs
                                //       type="text"
                                //       value={option.optionText}
                                //       placeholder="Option text"
                                //       onChange={(e) => handleChangeText(question.id, option.id, e.target.value)}
                                //       onBlur={() => {
                                //         // optionally clear hover flag
                                //       }}
                                //       onFocus={() => {
                                //         // optionally set hover flag
                                //       }}
                                //       isError={false}
                                //       myClassName="editable-label"
                                //       width={"100px"}
                                //       height="35px"
                                //       isFormControl={true}
                                //     />
                                //   ) : (
                                //     <span
                                //       onClick={() => {
                                //         // clicking text could focus edit - but keep simple
                                //       }}
                                //       className="label-text"
                                //     >
                                //       {option.optionText}
                                //     </span>
                                //   )
                                // }

                                label={
                                  option.isEditing ? (
                                    <CustomInputs
                                      type="text"
                                      value={option.optionText}
                                      placeholder="Option text"
                                      onChange={(e) => handleChangeText(question.id, option.id, e.target.value)}
                                      onBlur={() => {
                                        // Save and exit edit mode
                                        const updated = getQuestions().map((q) =>
                                          q.id === question.id
                                            ? {
                                              ...q,
                                              choices: q.choices.map((c) =>
                                                c.id === option.id ? { ...c, isEditing: false } : c
                                              ),
                                            }
                                            : q
                                        );
                                        updateQuestionsInState(updated);
                                      }}
                                      isError={false}
                                      myClassName="editable-label"
                                      width={"100px"}
                                      height="35px"
                                    />
                                  ) : (
                                    <span
                                      onClick={() => enableChoiceEdit(question.id, option.id)}
                                      className={TextCss.black.normal_text_400_14}
                                    >
                                      {option.optionText}
                                    </span>
                                  )
                                }

                                value={option.id}
                                name={`flexRadioDefault${question.id}`}
                                checked={
                                  question.questionTypeId === QuestionTypeEnum.SingleChoice
                                    ? question.followUpQuestionChoiceId === option.id
                                    : (question.selectedChoices || []).includes(option.id)
                                }
                                onChange={() =>
                                  handleOnClickElement(question.id, option.id, question.questionTypeId)
                                }
                                checkBoxClass="custom-radio-checkbox"
                              />

                              <div className="options_addRemove">
                                {question.questionTypeId === QuestionTypeEnum.SingleChoice && (<>
                                  <div className="CommonRoundBackgroundNoSize iconstyle24 BorderGrey BackgroundWhite">
                                    <AddTraspButton
                                      width={"10"}
                                      textclass={"sub_text fw-600 blue p-0"}
                                      bname={""}
                                      iconPosition={"right"}
                                      IconClassName={"iconstyle8"}
                                      onClick={() => handleShowFollowUpInput(question.id, option.id)}
                                      border={false}
                                      imageValue={GetImages.TildArow}
                                      disabled={disableFollowUpBtns}
                                    />
                                  </div>
                                  <div className="divider" />
                                  </>
                                )}

                                

                                <AddTraspButton
                                  width={0}
                                  textclass={"sub_text fw-600 grey p-0"}
                                  bname={""}
                                  IconClassName={"iconstyle12"}
                                  iconPosition={"right"}
                                  onClick={() => handleRemoveOption(question.id, option.id)}
                                  border={false}
                                  imageValue={GetImages.Close}
                                />
                              </div>
                            </div>

                            {/* Follow-up input appears only under the active choice */}
                            {isActiveFollowUp && question.questionTypeId === QuestionTypeEnum.SingleChoice && (
                              <div className="followup-box d-flex align-items-center gap-2" style={{ marginLeft: 36 }}>
                                <CustomInputs
                                  type="text"
                                  placeholder="Enter your follow up question"
                                  width={"50%"}
                                  value={question.followUpQuestionText || ""}
                                  onChange={(e) => handleChangeFollowup(question.id, e.target.value)}
                                  maxLength={250}
                                />
                                  <AddTraspButton
                                  type="button"
                                  width={0}
                                  textclass={"sub_text fw-600 grey p-0"}
                                  bname={""}
                                  IconClassName={"iconstyle12"}
                                  iconPosition={"right"}
                                  onClick={() => {
                                    const updated = getQuestions().map((q) =>
                                      q.id === question.id
                                        ? { ...q, followUpQuestionText: "", followUpQuestionChoiceId: null }
                                        : q
                                    );
                                    updateQuestionsInState(updated);
                                    handleHideFollowUp();
                                  }}
                                  border={false}
                                  imageValue={GetImages.Close}
                                />
                                {/* <button
                                  type="button"
                                  className="cross-btn"
                                  onClick={() => {
                                    // remove follow-up: clear followUpQuestionText and close UI
                                    const updated = getQuestions().map((q) =>
                                      q.id === question.id
                                        ? { ...q, followUpQuestionText: "", followUpQuestionChoiceId: null }
                                        : q
                                    );
                                    updateQuestionsInState(updated);
                                    handleHideFollowUp();
                                  }}
                                  aria-label="Remove follow up"
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: 20,
                                    lineHeight: 1,
                                  }}
                                >
                                  ✕
                                </button> */}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                <div className="optionsection">
                  <div className="options_addRemove">
                    {question.questionTypeId !== QuestionTypeEnum.ShortAnswer &&
                      question.questionTypeId !== QuestionTypeEnum.Paragraph && (
                        <>
                          <AddTraspButton
                            width={0}
                            textclass={"sub_text fw-600 blue p-0"}
                            bname={"Add Option"}
                            iconPosition={"right"}
                            onClick={() => handleAddOption(question.id)}
                            border={false}
                            imageValue={undefined}
                          />
                        </>
                      )}
                  </div>

                  <div className="options_buttons">
                    <AddTraspButton
                      width={0}
                      bname={undefined}
                      imageValue={GetImages.CopyBlue}
                      iconPosition={"right"}
                      onClick={() => copyQuestion(question.id)}
                      border={false}
                    />
                    <AddTraspButton
                      width={0}
                      bname={undefined}
                      imageValue={Delete}
                      iconPosition={"right"}
                      onClick={() => deleteQuestion(question.id)}
                      border={false}
                    />
                  </div>
                </div>


              </div>
            ))}

            <div className="addRemove" style={{ marginTop: 12 }}>
              <AddTraspButton
                width={0}
                bname={"Add another Question"}
                textclass={"sub_text fw-600 blue p-0"}
                iconPosition={undefined}
                onClick={handleAddQuestion}
                border={false}
              />
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default QuestionHistoryEdit;
